import React from 'react';
import { Loader } from '@growthaxl/web-foundation';
import { useEsaDomainStore } from '../../store/esaDomainStore';
import { useThreadMessages } from '../../hooks/useThreadMessages';
import { getMessageCache } from '../../cache/messageCacheInstance';
import { VirtualizedMessageList } from './VirtualizedMessageList';
import { TypingIndicator } from './TypingIndicator';
import { ESAMessageBubble } from '../messages/ESAMessageBubble';
import { ESAMessageErrorBoundary } from '../messages/ESAMessageErrorBoundary';
import { SolutionRunCard } from '../conversation/SolutionRunCard';
import { ESABriefing } from '../briefing/ESABriefing';
import { getConversationMessages } from '../../services/esaApi';
import { shellEventBus } from '../../../../shell/events/eventBus';
import type { ESACacheMessage } from '../../cache/MessageCache';
import { useConverseStatusStream } from '../../hooks/useConverseStatusStream';
import { ConverseStatusCard } from '../cards/ConverseStatusCard';
import { buildOpenCommitmentSettlements } from '../../utils/openCommitment';

export interface ESAChatViewProps {
  threadId?: string;
  onLoadMore?: (beforeMessageId: string) => Promise<void>;
  className?: string;
}

function mapApiMessage(m: {
  id: string;
  conversation_id: string;
  parent_message_id: string | null;
  initiator: 'user' | 'system';
  message_type: 'text' | 'json' | 'mix';
  message: Record<string, unknown>;
  has_attachments: boolean;
  stored_file_ukey: string | null;
  metad_created_at: string;
}): ESACacheMessage {
  // The backend stores user message content as a bare string, not { text }.
  // Normalise to { text } so ESAMessageBubble always renders without JSON quotes.
  const rawMsg = m.message as unknown;
  const message: Record<string, unknown> =
    typeof rawMsg === 'string' ? { text: rawMsg } : (rawMsg as Record<string, unknown>);
  return {
    id: m.id,
    conversationId: m.conversation_id,
    parentMessageId: m.parent_message_id,
    initiator: m.initiator,
    messageType: m.message_type,
    message,
    hasAttachments: m.has_attachments,
    storedFileUkey: m.stored_file_ukey,
    createdAt: m.metad_created_at,
  };
}

/**
 * Sort messages oldest-first by createdAt.
 * AgOps may return messages in DESC order depending on the query path; normalise
 * here so the cache is always ASC (oldest at index 0, newest at the end).
 */
function sortAsc(messages: ESACacheMessage[]): ESACacheMessage[] {
  return [...messages].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
}

/**
 * Renders the virtualized message list + typing indicator for the active thread.
 *
 * Initial load: when threadId changes and server history is not yet loaded for
 * it, fetches the first page of messages (getConversationMessages). SSE relay
 * injects may pre-seed the cache while the thread is inactive — those do not
 * count as a full history load.
 * Scroll-up pagination: handled by VirtualizedMessageList via handleLoadMore.
 *
 * The ESAComposer is lifted to EsaPanel so it persists across briefing/chat modes.
 */
const PAGE_SIZE = 150;

export function ESAChatView({ threadId: propThreadId, className }: ESAChatViewProps): React.ReactElement {
  const activeThreadId = useEsaDomainStore(s => s.activeThreadId);
  const streamStatus = useEsaDomainStore(s => s.streamStatus);
  const activeConverseClientSeq = useEsaDomainStore(s => s.activeConverseClientSeq);

  const threadId = propThreadId ?? activeThreadId;
  const cache = getMessageCache();
  const messages = useThreadMessages(threadId, cache);
  const isStreaming = streamStatus === 'connecting' || streamStatus === 'streaming';
  const { events: statusEvents } = useConverseStatusStream(threadId, {
    enabled: isStreaming,
    clientSeq: activeConverseClientSeq,
  });
  const showStatusCard = isStreaming && statusEvents.length > 0;

  const [isInitialLoading, setIsInitialLoading] = React.useState(false);

  // Tracks the lowest server-side offset already loaded for this thread.
  // Used to page backwards (scroll-up) correctly without re-fetching the same data.
  const serverBaseOffset = React.useRef(0);
  // Set to true once we have loaded back to offset=0 — no more older messages exist.
  const hasReachedHistoryStart = React.useRef(false);

  // ── Initial load ─────────────────────────────────────────────────────────
  // Fetch the first page (offset=0, oldest-first ASC from AgOps).
  // VirtualizedMessageList scrolls to the bottom on first paint so the user
  // always lands at the most recent message.
  React.useEffect(() => {
    if (!threadId) {
      setIsInitialLoading(false);
      return;
    }

    // Always reset pagination guards when the active thread changes.
    serverBaseOffset.current = 0;
    hasReachedHistoryStart.current = false;

    if (cache.isHistoryLoaded(threadId)) {
      // Important: a prior in-flight fetch for another/provisional thread may have
      // left loading=true; always clear when we skip because history is ready.
      setIsInitialLoading(false);
      return;
    }

    // Provisional ids are client-only; never hit the messages API for them.
    if (threadId.startsWith('prov-')) {
      setIsInitialLoading(false);
      return;
    }

    let cancelled = false;
    // Keep showing optimistic / SSE-seeded messages while history loads.
    if (cache.getSnapshot(threadId).length === 0) {
      setIsInitialLoading(true);
    }

    void (async () => {
      try {
        const result = await getConversationMessages(threadId, { offset: 0, limit: PAGE_SIZE });
        if (cancelled) return;
        const serverItems = sortAsc((result.items ?? []).map(mapApiMessage));
        // Merge with any SSE injects / optimistic sends pre-seeded before/during
        // fetch so setThread does not drop them.
        const live = cache.getSnapshot(threadId);
        const byId = new Map<string, ESACacheMessage>();
        for (const m of serverItems) byId.set(m.id, m);
        for (const m of live) {
          if (!byId.has(m.id)) byId.set(m.id, m);
        }
        const items = sortAsc([...byId.values()]);
        cache.setThread(threadId, items);
        cache.setPinnedThread(threadId);
        // If server returned fewer items than one page, we already have everything.
        if (serverItems.length < PAGE_SIZE) {
          hasReachedHistoryStart.current = true;
        }
      } catch {
        // Server unavailable — keep any optimistic/SSE cache; user can still type
      } finally {
        if (!cancelled) setIsInitialLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setIsInitialLoading(false);
    };
  }, [threadId, cache]);

  const handleLoadMore = React.useCallback(async (_beforeMessageId: string): Promise<void> => {
    if (!threadId) return;
    // Nothing older left to load.
    if (hasReachedHistoryStart.current) return;

    // Calculate previous page: step back from the current base.
    const prevBase = serverBaseOffset.current;
    const newOffset = Math.max(0, prevBase - PAGE_SIZE);
    const limit = prevBase - newOffset; // may be < PAGE_SIZE on the last backwards step

    try {
      const result = await getConversationMessages(threadId, { offset: newOffset, limit });
      // sortAsc so prepended older page is also in correct order.
      cache.prependHistory(threadId, sortAsc(result.items.map(mapApiMessage)));
      serverBaseOffset.current = newOffset;
      if (newOffset === 0) hasReachedHistoryStart.current = true;
    } catch {
      // Scroll-up pagination failure — silently ignore
    }
  }, [threadId, cache]);

  // No conversation selected (or an empty one) and nothing loading → show the
  // centered spark empty-state so the panel never renders a blank middle area.
  const showEmptyState = !isInitialLoading && messages.length === 0;

  // Dispatch a follow-up chip suggestion as a new user message via the shared
  // ESA_SEND_MESSAGE bus event. This routes through useEsaBusSendMessage in
  // EsaPanel (the authoritative sendMessage owner) so the conversational flow,
  // optimistic message display, and thread management are all handled correctly
  // without duplicating useEsaConverse here.
  const handleFollowUpSelect = React.useCallback((suggestion: string): void => {
    shellEventBus.dispatch({ type: 'ESA_SEND_MESSAGE', payload: { message: suggestion } });
  }, []);

  // For approval_card options, send the selected option text as a message.
  // The hilRequestId is included in the message text so the agent can correlate it.
  const handleApprovalOptionSelect = React.useCallback((option: string): void => {
    shellEventBus.dispatch({ type: 'ESA_SEND_MESSAGE', payload: { message: option } });
  }, []);

  // Opens the canvas message-expand overlay for long plain-text messages.
  const handleExpand = React.useCallback((msg: ESACacheMessage): void => {
    useEsaDomainStore.getState().setExpandedMessage(msg);
  }, []);

  // Open asks the user has already replied past render as a settled record
  // instead of live options, so an earlier turn cannot be answered twice.
  const openCommitmentSettlements = React.useMemo(
    () => buildOpenCommitmentSettlements(messages),
    [messages],
  );

  return (
    <div className={`flex flex-col flex-1 overflow-hidden ${className ?? ''}`}>
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {isInitialLoading ? (
          /* Skeleton while first page loads from server */
          <div className="h-full flex items-center justify-center opacity-50" aria-label="Loading conversation">
            <Loader />
          </div>
        ) : showEmptyState ? (
          <ESABriefing />
        ) : (
          <VirtualizedMessageList
            messages={messages}
            onLoadMore={handleLoadMore}
            className="h-full py-4 px-3"
            scrollToBottomKey={showStatusCard ? statusEvents.length : 'idle'}
            renderMessage={(msg) => (
              <ESAMessageErrorBoundary key={msg.id} messageId={msg.id}>
                <ESAMessageBubble
                  message={msg}
                  className="mb-4"
                  onFollowUpSelect={handleFollowUpSelect}
                  onApprovalOptionSelect={handleApprovalOptionSelect}
                  onOpenCommitmentSelect={handleFollowUpSelect}
                  openCommitmentSettledWith={openCommitmentSettlements.get(msg.id)}
                  onExpand={handleExpand}
                />
              </ESAMessageErrorBoundary>
            )}
            trailingContent={
              /* Wrap in the same flex shell as ESAMessageBubble so the card's
               * left edge aligns with message-bubble content (avatar 22px + gap-2 8px). */
              <div className="flex gap-2 items-start mb-4">
                <div className="shrink-0 w-[22px]" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <SolutionRunCard />
                </div>
              </div>
            }
          />
        )}
      </div>
      <ConverseStatusCard events={statusEvents} visible={isStreaming} />
      {/* Typing dots while waiting for first status frame or when SSE is quiet */}
      <TypingIndicator visible={isStreaming && !showStatusCard} />
    </div>
  );
}
